import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Typography,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Fade,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  CloudUpload as UploadIcon,
  SkipNext as SkipIcon,
  Lightbulb as LightbulbIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  Factory as FactoryIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
}

interface ExtractedData {
  products?: any[];
  suppliers?: any[];
  facilities?: any[];
  inventory?: any;
  bom?: any[];
  logistics?: any;
}

const SmartOnboardingPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState('welcome');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [extractedData, setExtractedData] = useState<ExtractedData>({});
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const steps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welcome',
      description: 'Company profile',
      icon: <BusinessIcon />,
      completed: false
    },
    {
      id: 'products',
      title: 'Products',
      description: 'What you sell',
      icon: <CategoryIcon />,
      completed: false
    },
    {
      id: 'suppliers',
      title: 'Suppliers',
      description: 'Component sources',
      icon: <ShippingIcon />,
      completed: false
    },
    {
      id: 'facilities',
      title: 'Facilities',
      description: 'Manufacturing locations',
      icon: <FactoryIcon />,
      completed: false
    },
    {
      id: 'inventory',
      title: 'Inventory',
      description: 'Stock levels',
      icon: <InventoryIcon />,
      completed: false
    }
  ];

  useEffect(() => {
    // Initial welcome message
    setMessages([{
      id: 'welcome',
      type: 'ai',
      content: "👋 Welcome to ASCIN! I'm your AI assistant.\n\n" +
               "I'll help you set up your supply chain in just a few minutes. " +
               "We can have a natural conversation - just tell me about your business!\n\n" +
               "Let's start: What's your company name?",
      timestamp: new Date()
    }]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputMessage.trim();
    
    if (!textToSend) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      // TODO: Call smart onboarding API
      const response = await fetch('/api/v1/smart-onboarding/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          message: textToSend,
          step: currentStep,
          context: {
            completed_steps: steps.filter(s => s.completed).map(s => s.id),
            extracted_data: extractedData
          }
        })
      });

      const data = await response.json();

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      
      if (data.extracted_data) {
        setExtractedData(prev => ({
          ...prev,
          ...data.extracted_data
        }));
      }

      if (data.suggestions) {
        setSuggestions(data.suggestions);
      }

      if (data.next_step) {
        setCurrentStep(data.next_step);
      }

      if (data.completion_percentage !== undefined) {
        setCompletionPercentage(data.completion_percentage);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: "I'm sorry, I encountered an error. Please try again.",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputMessage(suggestion);
    handleSendMessage(suggestion);
  };

  const handleSkipStep = () => {
    const nextStepIndex = steps.findIndex(s => s.id === currentStep) + 1;
    if (nextStepIndex < steps.length) {
      setCurrentStep(steps[nextStepIndex].id);
      
      const skipMessage: Message = {
        id: Date.now().toString(),
        type: 'ai',
        content: `No problem! We can come back to that later. Let's move on to ${steps[nextStepIndex].title}.`,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, skipMessage]);
    }
  };

  const handleUploadCSV = () => {
    setShowUploadDialog(true);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  return (
    <Container maxWidth="xl" sx={{ height: 'calc(100vh - 100px)', py: 2 }}>
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Left Panel - Progress & Data Summary */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Setup Progress
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {completionPercentage}% Complete
                </Typography>
                <Typography variant="body2" color="primary">
                  {steps.filter(s => s.completed).length}/{steps.length} steps
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={completionPercentage} 
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <List>
              {steps.map((step, index) => (
                <ListItem 
                  key={step.id}
                  sx={{ 
                    bgcolor: currentStep === step.id ? 'action.selected' : 'transparent',
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  <ListItemIcon>
                    {step.completed ? (
                      <CheckIcon color="success" />
                    ) : currentStep === step.id ? (
                      <Box sx={{ color: 'primary.main' }}>{step.icon}</Box>
                    ) : (
                      <UncheckedIcon color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={step.title}
                    secondary={step.description}
                    primaryTypographyProps={{
                      fontWeight: currentStep === step.id ? 'bold' : 'normal'
                    }}
                  />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Data Summary */}
            <Typography variant="subtitle2" gutterBottom>
              Data Collected
            </Typography>
            
            <Box sx={{ mt: 1 }}>
              {extractedData.products && (
                <Chip 
                  label={`${extractedData.products.length} Products`}
                  size="small"
                  color="primary"
                  sx={{ mr: 1, mb: 1 }}
                />
              )}
              {extractedData.suppliers && (
                <Chip 
                  label={`${extractedData.suppliers.length} Suppliers`}
                  size="small"
                  color="secondary"
                  sx={{ mr: 1, mb: 1 }}
                />
              )}
              {extractedData.facilities && (
                <Chip 
                  label={`${extractedData.facilities.length} Facilities`}
                  size="small"
                  color="info"
                  sx={{ mr: 1, mb: 1 }}
                />
              )}
            </Box>

            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleUploadCSV}
                sx={{ mb: 1 }}
              >
                Import CSV
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SkipIcon />}
                onClick={handleSkipStep}
              >
                Skip Step
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Right Panel - Chat Interface */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h5">
                Smart Onboarding
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tell me about your supply chain in your own words
              </Typography>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
              {messages.map((message) => (
                <Fade in key={message.id} timeout={300}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                        maxWidth: '80%'
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: message.type === 'user' ? 'primary.main' : 'secondary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 1,
                          flexShrink: 0
                        }}
                      >
                        {message.type === 'user' ? (
                          <PersonIcon sx={{ color: 'white' }} />
                        ) : (
                          <BotIcon sx={{ color: 'white' }} />
                        )}
                      </Box>
                      
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          bgcolor: message.type === 'user' ? 'primary.light' : 'white',
                          color: message.type === 'user' ? 'primary.contrastText' : 'text.primary'
                        }}
                      >
                        <Typography 
                          variant="body1" 
                          sx={{ whiteSpace: 'pre-line' }}
                        >
                          {message.content}
                        </Typography>
                        
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ display: 'block', mt: 1 }}
                        >
                          {message.timestamp.toLocaleTimeString()}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                </Fade>
              ))}
              
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'secondary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 1
                      }}
                    >
                      <BotIcon sx={{ color: 'white' }} />
                    </Box>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Typography variant="body2">Thinking...</Typography>
                    </Paper>
                  </Box>
                </Box>
              )}
              
              <div ref={messagesEndRef} />
            </Box>

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LightbulbIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">
                    Suggestions:
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {suggestions.map((suggestion, idx) => (
                    <Chip
                      key={idx}
                      label={suggestion}
                      onClick={() => handleSuggestionClick(suggestion)}
                      clickable
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Input */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder="Type your message here..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  variant="outlined"
                />
                <IconButton
                  color="primary"
                  onClick={() => handleSendMessage()}
                  disabled={loading || !inputMessage.trim()}
                  sx={{ alignSelf: 'flex-end' }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Press Enter to send, Shift+Enter for new line
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onClose={() => setShowUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Upload CSV files to quickly import your supply chain data.
          </Typography>
          
          <Button
            fullWidth
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            sx={{ mb: 2 }}
          >
            Upload Suppliers CSV
            <input type="file" hidden accept=".csv" />
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            sx={{ mb: 2 }}
          >
            Upload Products CSV
            <input type="file" hidden accept=".csv" />
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
          >
            Upload Facilities CSV
            <input type="file" hidden accept=".csv" />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setShowUploadDialog(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SmartOnboardingPage;
